import { parseIsoDate, sameDay } from './date';
import type { Locale } from './i18n';

// numeric dates are shown day/month/year regardless of the UI language
const NUMERIC_DATE_LOCALE = 'en-GB';

export class Formatter {
  readonly locale: Locale;

  constructor({ locale }: { locale: Formatter['locale'] }) {
    this.locale = locale;
  }

  currency(value: number | string): string {
    return new Intl.NumberFormat(this.locale, {
      style: 'currency',
      currency: 'EUR',
    }).format(Number(value));
  }

  singleDigitNumber(value: number): string {
    return new Intl.NumberFormat(this.locale, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value);
  }

  hours(value: number): string {
    return new Intl.NumberFormat(this.locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(value);
  }

  date(value: Date | string): string | null {
    const date = this.parseDate(value);

    if (!date) {
      return null;
    }
    return new Intl.DateTimeFormat(NUMERIC_DATE_LOCALE, {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    }).format(date);
  }

  dayName(value: Date | string): string | null {
    const date = this.parseDate(value);
    if (!date) {
      return null;
    }
    return new Intl.DateTimeFormat(this.locale, {
      weekday: 'long',
    }).format(date);
  }

  weekdayShort(value: Date | string): string | null {
    const date = this.parseDate(value);
    if (!date) {
      return null;
    }
    return new Intl.DateTimeFormat(this.locale, {
      weekday: 'short',
    }).format(date);
  }

  monthAndYear(value: Date | string): string | null {
    const date = this.parseDate(value);
    if (!date) {
      return null;
    }
    return new Intl.DateTimeFormat(this.locale, {
      month: 'long',
      year: 'numeric',
    }).format(date);
  }

  monthShortAndYear(value: Date | string): string | null {
    const date = this.parseDate(value);
    if (!date) {
      return null;
    }
    return new Intl.DateTimeFormat(this.locale, {
      month: 'short',
      year: 'numeric',
    }).format(date);
  }

  monthShort(value: Date | string): string | null {
    const date = this.parseDate(value);
    if (!date) {
      return null;
    }
    return new Intl.DateTimeFormat(this.locale, {
      month: 'short',
    }).format(date);
  }

  dayAndShortMonth(value: Date | string): string | null {
    const date = this.parseDate(value);
    if (!date) {
      return null;
    }
    return new Intl.DateTimeFormat(NUMERIC_DATE_LOCALE, {
      day: 'numeric',
      month: 'short',
    }).format(date);
  }

  monthName(value: Date | string): string | null {
    const date = this.parseDate(value);
    if (!date) {
      return null;
    }
    return new Intl.DateTimeFormat(this.locale, {
      month: 'long',
    }).format(date);
  }

  time(value: Date | string): string | null {
    const date = this.parseDate(value);
    if (!date) {
      return null;
    }
    return new Intl.DateTimeFormat(this.locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  }

  datetime(value: Date | string): string | null {
    const date = this.parseDate(value);
    if (!date) {
      return null;
    }
    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      weekday: 'long',
    };
    // if the date was more than 1 week ago
    if (date.getTime() < Date.now() - 7 * 24 * 60 * 60 * 1000) {
      Object.assign(options, {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
    } else {
      Object.assign(options, { weekday: 'long' });
    }

    return new Intl.DateTimeFormat(this.locale, options).format(date);
  }

  dateLongMonth(value: Date | string): string | null {
    const date = this.parseDate(value);
    if (!date) {
      return null;
    }
    return new Intl.DateTimeFormat(this.locale, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }

  narrowWeek(value: Date | string): string | null {
    const date = this.parseDate(value);
    if (!date) {
      return null;
    }
    return new Intl.DateTimeFormat(this.locale, {
      weekday: 'narrow',
    }).format(date);
  }

  dateLongNoYear(value: Date | string) {
    const date = this.parseDate(value);
    if (!date) {
      return null;
    }
    return new Intl.DateTimeFormat(this.locale, {
      month: 'long',
      day: 'numeric',
    }).format(date);
  }

  dateNoYear(value: Date | string) {
    const date = this.parseDate(value);
    if (!date) {
      return null;
    }
    return new Intl.DateTimeFormat(NUMERIC_DATE_LOCALE, {
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  }

  // ISO days grouped by month, "Dec 30, 31, Jan 1, 2"
  dayList(days: string[]): string {
    // "short" units read "7, 8, 9"; the narrow style drops the commas
    const list = new Intl.ListFormat(this.locale, {
      style: 'short',
      type: 'unit',
    });
    const groups: string[][] = [];
    days.forEach((day) => {
      const group = groups[groups.length - 1];
      if (group && group[0].slice(0, 7) === day.slice(0, 7)) {
        group.push(day);
      } else {
        groups.push([day]);
      }
    });
    return list.format(
      groups.map((group) => {
        const numbers = group.map((day) => String(Number(day.slice(8, 10))));
        return `${this.monthShort(group[0]) ?? ''} ${list.format(numbers)}`;
      })
    );
  }

  dateRangeLong(start: Date | string, end: Date | string): string | null {
    const startDate = this.parseDate(start);
    const endDate = this.parseDate(end);
    if (!startDate || !endDate) {
      return null;
    }
    if (sameDay(startDate, endDate)) {
      return this.date(startDate);
    }
    if (startDate.getFullYear() === endDate.getFullYear()) {
      return `${this.dateLongNoYear(startDate)} - ${this.dateLongMonth(endDate)}`;
    } else {
      return `${this.dateLongMonth(startDate)} - ${this.dateLongMonth(endDate)}`;
    }
  }

  dateRange(start: Date | string, end: Date | string): string | null {
    const startDate = this.parseDate(start);
    const endDate = this.parseDate(end);
    if (!startDate || !endDate) {
      return null;
    }
    if (sameDay(startDate, endDate)) {
      return this.date(startDate);
    }
    if (startDate.getFullYear() === endDate.getFullYear()) {
      return `${this.dateNoYear(startDate)} - ${this.date(endDate)}`;
    } else {
      return `${this.date(startDate)} - ${this.date(endDate)}`;
    }
  }

  timeRange(start: Date | string, end: Date | string): string | null {
    const startDate = this.parseDate(start);
    const endDate = this.parseDate(end);
    if (!startDate || !endDate) {
      return null;
    }
    return `${this.time(startDate)} - ${this.time(endDate)}`;
  }

  parseDate(value?: string | Date | null): Date | null {
    if (!value) {
      return null;
    }
    if (typeof value !== 'string') {
      return value;
    }
    // a bare day is a local date; Date.parse would read it as UTC midnight and
    // show the previous day west of UTC
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return parseIsoDate(value);
    }
    const unix = Date.parse(value);
    if (isNaN(unix)) {
      return null;
    }
    return new Date(unix);
  }

  parseRequiredDate(value: string | Date | null): Date {
    const date = this.parseDate(value);
    if (!date) {
      throw new Error('Invalid date');
    }
    return date;
  }

  percentage(value: number): string {
    return new Intl.NumberFormat(this.locale, {
      style: 'percent',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(value);
  }
}
